import { useCallback, useEffect, useMemo, useState } from "react";
import { AgentIcon } from "../../components/Dimension.jsx";
import HoverTooltip from "../../components/HoverTooltip.jsx";
import { SkeletonBar, SkeletonLines } from "../../components/ui/Skeleton.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import {
    RELEASE_STATE,
    getManagedAgentConfig,
    getManagedAgentConfigRelease,
    getManagedConfigFleetSummary,
    getManagedConfigReportingDevices,
    listManagedAgentConfigVersions,
    managedConfigFleetCategory,
    managedConfigFleetOutcomeCounts,
} from "../../lib/managedConfigClient.js";
import { StatusMark } from "./controls.jsx";
import { formatDate } from "./format.js";
import { removalStatus } from "./removalStatus.js";
import { PRIMARY_ACTION, SECONDARY_ACTION } from "./styles.js";
import { pilotProgress, plural } from "./StepStrip.jsx";

const ATTENTION_ORDER = ["unloadable", "apply-failed", "status-unavailable", "shadowed", "drifted", "no-agent-support", "stale-generation"];
const ATTENTION_LABELS = {
    unloadable: "managed settings file refused",
    "apply-failed": "apply failures",
    "status-unavailable": "status unavailable",
    shadowed: "shadowed",
    drifted: "drifted",
    "no-agent-support": "Origin Agent support not reported",
    "stale-generation": "not updated yet",
};

function isConfigured(current) {
    // The current-version pointer includes saved and restored policies without release signatures.
    return Boolean(Number(current?.generation) > 0 && !current.unmanage);
}

function isSignedPolicy(version) {
    return Boolean(version?.signedAt && isConfigured(version));
}

function settled(result) {
    return result.status === "fulfilled" ? result.value : null;
}

function isNotFound(result) {
    return result.status === "rejected"
        && (result.reason?.status === 404 || result.reason?.grpcCode === "NOT_FOUND");
}

async function loadAgent(definition) {
    const [fleetResult, policyResult, releaseResult, versionsResult] = await Promise.allSettled([
        getManagedConfigFleetSummary(definition.vendor, { agentLabel: definition.label }),
        getManagedAgentConfig(definition.vendor),
        getManagedAgentConfigRelease(definition.vendor),
        listManagedAgentConfigVersions(definition.vendor),
    ]);
    const current = settled(policyResult)?.currentVersion ?? null;
    const policyUnavailable = policyResult.status === "rejected" && !isNotFound(policyResult);
    const history = settled(versionsResult);
    return {
        definition,
        fleet: settled(fleetResult),
        current,
        policyUnavailable,
        enabled: settled(policyResult)?.enabled ?? settled(fleetResult)?.deliveryEnabled ?? false,
        release: settled(releaseResult)?.release ?? null,
        releaseUnavailable: releaseResult.status === "rejected" && !isNotFound(releaseResult),
        latestSigned: history?.find(isSignedPolicy) ?? (isSignedPolicy(current) ? current : null),
        historyUnavailable: versionsResult.status === "rejected" && !isNotFound(versionsResult),
        historyLimitReached: history?.length === 50,
    };
}

const TONE_FILL = {
    good: "bg-[color:var(--green)]",
    warning: "bg-[#aa742f]",
    danger: "bg-[#aa4c43]",
    neutral: "bg-[color:var(--muted-soft)]",
};
const FOCUS_RING = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--green)]";
const LABEL_CLASS = "text-[12px] leading-[1.2] text-[color:var(--muted)]";

function categoryTone(category) {
    return category.tone ?? managedConfigFleetCategory(category.id)?.tone ?? "neutral";
}

function SummaryTile({ label, value, children }) {
    return (
        <div className="grid content-start gap-[10px] border-r border-[color:var(--line)] p-[16px] last:border-r-0 max-sm:border-r-0 max-sm:border-b max-sm:last:border-b-0">
            <dt className={LABEL_CLASS}>{label}</dt>
            <dd className="m-0 text-[30px] leading-none font-medium tabular-nums tracking-[-0.04em] text-[color:var(--ink)]">{value}</dd>
            {children}
        </div>
    );
}

function ConfiguredAgentIcons({ agents, rows, loading, onNavigate }) {
    return <span className="inline-flex items-center gap-[6px]" role="group" aria-label="Policy status by agent type">
        {agents.map((agent) => {
            const item = rows.find((row) => row.definition.id === agent.id);
            const configured = isConfigured(item?.current);
            const status = loading ? "Policy status loading" : item?.policyUnavailable || !item ? "Policy status unavailable" : configured ? item.enabled ? "Current policy, delivery active" : "Current policy, delivery paused" : "No current policy";
            return <HoverTooltip key={agent.id} label={`${agent.label}: ${status}`} placement="top">
                <button
                    type="button"
                    aria-label={`${agent.label}: ${status}`}
                    onClick={() => onNavigate(agent.id, "policy")}
                    className={`flex h-[28px] w-[28px] items-center justify-center rounded-[7px] border ${FOCUS_RING} ${configured ? "border-[color:var(--green)] bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[color:var(--green)] hover:text-[color:var(--ink)]" : "border-[color:var(--line)] text-[color:var(--muted-soft)] opacity-50 hover:opacity-80"}`}
                >
                    <AgentIcon name={agent.label} className="h-[16px] w-[16px]" />
                </button>
            </HoverTooltip>;
        })}
    </span>;
}

function VendorEndpointCounts({ agents, devices, loading }) {
    return <span className="flex flex-wrap gap-x-[16px] gap-y-[4px]" role="group" aria-label="Endpoints with managed configurations by agent type">
        {agents.map((agent) => {
            const counts = devices?.knownDistinctDevices == null ? devices?.vendorEndpointCounts : devices?.knownVendorEndpointCounts;
            const value = counts?.find((count) => count.vendor === agent.vendor)?.endpoints;
            return <span key={agent.id} className="inline-flex items-center gap-[6px] text-[11px] text-[color:var(--muted)]">
                <AgentIcon name={agent.label} className="h-[12px] w-[12px]" />
                <span>{agent.label}</span>
                <span className="tabular-nums text-[color:var(--ink)]">{loading ? <SkeletonBar className="w-[20px]" /> : value == null ? "Unavailable" : value.toLocaleString()}</span>
            </span>;
        })}
    </span>;
}

function Fact({ label, value, detail, onClick }) {
    return (
        <button type="button" onClick={onClick} className={`group grid min-w-0 content-start gap-[5px] p-[12px] text-left hover:bg-[color:var(--control-bg)] ${FOCUS_RING}`}>
            <span className={LABEL_CLASS}>{label}</span>
            <span className="text-[15px] leading-[1.1] font-medium text-[color:var(--ink)] group-hover:text-[color:var(--green)]">{value}</span>
            {detail ? <span className="text-[11px] leading-[1.25] text-[color:var(--muted)]">{detail}</span> : null}
        </button>
    );
}

// A summary figure not yet read, the height of the figure.
function FigurePending() {
    return <span className="flex h-[30px] items-center"><SkeletonBar className="w-[56px]" /></span>;
}

// An agent's card while the agents are read: its header (the agent is known
// before its policy is) over a few lines, so the grid is laid out before the
// cards land.
function AgentCardLoading({ definition }) {
    return (
        <article aria-hidden="true" className="grid content-start rounded-[10px] border border-dashed border-[color:var(--line)] bg-[color:var(--bg)]">
            <header className="flex flex-wrap items-center justify-between gap-[10px] border-b border-[color:var(--line)] px-[14px] py-[12px]">
                <div className="flex items-center gap-[10px]">
                    <span className="flex h-[30px] w-[30px] items-center justify-center rounded-[7px] border border-[color:var(--line)] text-[color:var(--muted-soft)]">
                        <AgentIcon name={definition.label} className="h-[16px] w-[16px]" />
                    </span>
                    <div className="grid gap-[3px]">
                        <h3 className="m-0 text-[15px] leading-none font-medium tracking-[-0.02em]">{definition.label}</h3>
                        <span className="flex h-[15px] items-center"><SkeletonBar className="w-[80px]" /></span>
                    </div>
                </div>
                <SkeletonBar className="w-[96px]" />
            </header>
            <div className="p-[14px]"><SkeletonLines lines={2} /></div>
        </article>
    );
}

function agentState(item) {
    const configured = isConfigured(item.current);
    const pilot = item.release?.state === RELEASE_STATE.PILOTING;
    if (item.policyUnavailable) return { tone: "danger", label: "Policy unavailable" };
    if (item.current?.unmanage && pilot) return {
        tone: "neutral",
        label: "Pilot active",
        detail: `Non-pilot endpoints should have no policy (removed in v${item.current.generation}). Pilot endpoints should run v${item.release.version?.generation ?? "?"}.`,
    };
    if (item.current?.unmanage) return removalStatus(item.current, item.fleet);
    if (configured) return item.enabled ? { tone: "good", label: "Delivery active" } : { tone: "warning", label: "Delivery paused" };
    if (pilot) return { tone: "neutral", label: "Pilot only" };
    return { tone: "neutral", label: "No policy" };
}

function AgentCard({ item, onNavigate }) {
    const { definition, current, fleet, release, latestSigned } = item;
    const configured = isConfigured(current);
    const pilot = release?.state === RELEASE_STATE.PILOTING ? release : null;
    const progress = pilot ? pilotProgress(pilot) : null;
    const state = agentState(item);
    const to = (view, category) => onNavigate(definition.id, view, category);
    const showFacts = configured || current?.unmanage || pilot;
    const outcomes = managedConfigFleetOutcomeCounts(fleet);
    // A directly saved or restored current policy may have no release signature.
    const showLatestSigned = !configured || latestSigned?.generation !== current?.generation;

    return (
        <article aria-label={definition.label} className={`grid content-start rounded-[10px] border bg-[color:var(--bg)] ${configured ? "border-[color:var(--line-strong)]" : "border-dashed border-[color:var(--line)]"}`}>
            <header className="flex flex-wrap items-center justify-between gap-[10px] border-b border-[color:var(--line)] px-[14px] py-[12px]">
                <div className="flex items-center gap-[10px]">
                    <span className={`flex h-[30px] w-[30px] items-center justify-center rounded-[7px] border ${configured ? "border-[color:var(--green)] bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[color:var(--green)]" : "border-[color:var(--line)] text-[color:var(--muted-soft)]"}`}>
                        <AgentIcon name={definition.label} className="h-[16px] w-[16px]" />
                    </span>
                    <div className="grid gap-[3px]">
                        <h3 className="m-0 text-[15px] leading-none font-medium tracking-[-0.02em]">{definition.label}</h3>
                        <StatusMark tone={state.tone}>{state.label}</StatusMark>
                    </div>
                </div>
                <button type="button" className={configured || item.policyUnavailable ? SECONDARY_ACTION : PRIMARY_ACTION} onClick={() => to("policy")}>{configured || current?.unmanage || item.policyUnavailable ? "Draft new policy" : pilot ? "Open pilot" : "Configure policy"}</button>
            </header>

            {showFacts ? <div className={`grid ${showLatestSigned ? "grid-cols-3" : "grid-cols-2"} divide-x divide-[color:var(--line)] border-b border-[color:var(--line)] max-sm:grid-cols-1 max-sm:divide-x-0 max-sm:divide-y`}>
                <Fact
                    label="Current policy"
                    value={current?.unmanage && Number(current.generation) > 0 ? `None (removed in v${current.generation})` : configured ? `v${current.generation}` : "None"}
                    detail={configured ? current.signedAt ? `Signed ${formatDate(current.signedAt)}` : current.createdAt ? `Saved ${formatDate(current.createdAt)}` : null : null}
                    onClick={() => to("policy")}
                />
                {showLatestSigned ? <Fact
                    label="Latest signed release"
                    value={latestSigned ? `v${latestSigned.generation}` : item.historyUnavailable ? "Unavailable" : item.historyLimitReached ? "Outside recent history" : "None"}
                    detail={latestSigned?.signedAt ? formatDate(latestSigned.signedAt) : null}
                    onClick={() => to("releases")}
                /> : null}
                <Fact
                    label="Active pilot"
                    value={pilot ? `v${pilot.version?.generation ?? "?"}` : item.releaseUnavailable ? "Unavailable" : "None"}
                    detail={progress ? `${progress.confirmed} of ${progress.total - progress.excluded} confirmed${progress.excluded ? `, ${progress.excluded} excluded` : ""}` : null}
                    onClick={() => to(pilot ? "pilot" : "policy")}
                />
            </div> : null}

            {configured || current?.unmanage ? <section aria-label={`${definition.label} fleet`} className="grid gap-[10px] p-[14px]">
                <div className="flex flex-wrap items-baseline justify-between gap-[8px]">
                    <span className="text-[12px] font-medium">Fleet compliance{fleet ? ` - ${plural(fleet.knownTotal ?? fleet.total ?? 0, "endpoint")}` : ""}</span>
                    {fleet?.observedAt ? <span className="text-[10px] text-[color:var(--muted)]">Checked {formatDate(fleet.observedAt)}</span> : null}
                </div>
                {fleet ? <>
                    <div className="grid grid-cols-2 divide-x divide-[color:var(--line)] border-y border-[color:var(--line)]">
                        {[["effective", "Effective"], ["ineffective", "Ineffective"]].map(([id, label]) => <button key={id} type="button" onClick={() => to("fleet", id)} className={`grid gap-[3px] p-[12px] text-left hover:bg-[color:var(--control-bg)] ${FOCUS_RING}`}><span className="text-[10px] text-[color:var(--muted)]">{label}</span><strong className="text-[22px] font-medium tabular-nums leading-none">{outcomes[id].toLocaleString()}</strong></button>)}
                    </div>
                    <div className="flex flex-wrap gap-x-[16px] gap-y-[4px] text-[11px]">
                        {[["awaiting", "of these not updated yet"], ["unverified", "unable to verify"]].map(([id, label]) => <button key={id} type="button" onClick={() => to("fleet", id)} className={`text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:underline ${FOCUS_RING}`}><span className="tabular-nums">{outcomes[id].toLocaleString()}</span> {label}</button>)}
                    </div>
                    {current?.unmanage ? <p className={`${NOTE_CLASS} m-0`}>{state.detail}</p> : null}
                </> : <p className={`${NOTE_CLASS} m-0`}>{current?.unmanage ? state.detail : "Fleet status could not be loaded. Refresh to try again."}</p>}
            </section> : <div className="p-[14px]">
                <p className={`${NOTE_CLASS} m-0 text-[12px]`}>{item.policyUnavailable
                    ? "The policy could not be loaded. Refresh to try again."
                    : current?.unmanage ? state.detail
                    : pilot ? "A pilot is in progress. The fleet gets this policy when the release is signed."
                    : `No current policy. ${definition.label} endpoints get no managed configuration.`}</p>
            </div>}
        </article>
    );
}

export default function AllAgentsOverview({ agents, onNavigate }) {
    const [snapshot, setSnapshot] = useState({ loading: true, rows: [], devices: null });
    const [refreshKey, setRefreshKey] = useState(0);
    const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

    useEffect(() => {
        let live = true;
        setSnapshot((previous) => ({ ...previous, loading: true }));
        Promise.all([
            Promise.all(agents.map(loadAgent)),
            getManagedConfigReportingDevices().catch(() => null),
        ]).then(([rows, devices]) => {
            if (live) setSnapshot({ loading: false, rows, devices });
        });
        return () => { live = false; };
    }, [agents, refreshKey]);

    const { rows, devices, loading } = snapshot;
    // Only the first read has nothing to show; a refresh keeps the last figures.
    const first = loading && !rows.length;
    const configured = rows.filter((item) => isConfigured(item.current));
    const policyComplete = rows.length === agents.length && rows.every((item) => !item.policyUnavailable);
    const summariesComplete = policyComplete && configured.every((item) => item.fleet);
    const attention = useMemo(() => configured.flatMap((item) => ATTENTION_ORDER.map((id, rank) => {
        const category = item.fleet?.categories?.find((entry) => entry.id === id);
        return { agent: item.definition, id, rank, count: category?.count ?? 0, tone: category ? categoryTone(category) : "neutral" };
    })).filter((item) => item.count > 0).sort((a, b) => a.rank - b.rank || b.count - a.count || a.agent.label.localeCompare(b.agent.label)).slice(0, 6), [configured]);

    return (
        <div className="grid gap-[20px] pb-[24px]" aria-busy={loading ? "true" : "false"}>
            {/* The page's own title already says Agent policy; this part only names itself for assistive tech. */}
            <header className="flex flex-wrap items-end justify-end gap-[10px]">
                <h2 className="sr-only">All agents</h2>
                <button type="button" className={SECONDARY_ACTION} onClick={refresh} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
            </header>

            <dl className="m-0 grid rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] sm:grid-cols-2" aria-label="Summary">
                <SummaryTile label="Agents with a configured policy" value={first ? <FigurePending /> : policyComplete ? <>{configured.length.toLocaleString()}<span className="text-[16px] text-[color:var(--muted)]"> of {agents.length.toLocaleString()}</span></> : "Unavailable"}>
                    <ConfiguredAgentIcons agents={agents} rows={rows} loading={loading} onNavigate={onNavigate} />
                </SummaryTile>
                <SummaryTile label="Endpoints with managed configurations" value={first ? <FigurePending /> : (devices?.knownDistinctDevices ?? devices?.distinctDevices) == null ? "Unavailable" : (devices.knownDistinctDevices ?? devices.distinctDevices).toLocaleString()}>
                    <VendorEndpointCounts agents={agents} devices={devices} loading={first} />
                </SummaryTile>
            </dl>

            {attention.length || (!loading && !summariesComplete) ? <section aria-labelledby="all-attention-title" className="grid gap-[8px]">
                <h2 id="all-attention-title" className="m-0 text-[15px] font-medium">Needs attention</h2>
                <ul className="m-0 grid list-none overflow-hidden rounded-[10px] border border-[#aa742f]/60 bg-[#aa742f]/5 p-0">
                    {attention.map((item) => <li key={`${item.agent.id}-${item.id}`} className="border-t border-[color:var(--line)] first:border-t-0">
                        <button type="button" className={`flex w-full items-center justify-between gap-[12px] px-[14px] py-[10px] text-left hover:bg-[color:var(--control-bg)] ${FOCUS_RING} focus-visible:-outline-offset-2`} onClick={() => onNavigate(item.agent.id, "fleet", item.id)}>
                            <span className="inline-flex items-center gap-[10px] text-[12px]">
                                <span className={`h-[7px] w-[7px] rounded-full ${TONE_FILL[item.tone]}`} />
                                <AgentIcon name={item.agent.label} className="h-[14px] w-[14px] text-[color:var(--muted)]" />
                                <span>{item.agent.label}: <strong className="font-medium">{item.count.toLocaleString()} {ATTENTION_LABELS[item.id]}</strong></span>
                            </span>
                            <span className="text-[11px] text-[color:var(--green)]">View endpoints</span>
                        </button>
                    </li>)}
                    {!loading && !summariesComplete ? <li className={`${NOTE_CLASS} border-t border-[color:var(--line)] px-[14px] py-[10px] first:border-t-0`}>Some fleet checks could not be loaded. Refresh to try again.</li> : null}
                </ul>
            </section> : null}

            <section aria-labelledby="all-agents-title" className="grid gap-[8px]">
                <h2 id="all-agents-title" className="m-0 text-[15px] font-medium">Agents</h2>
                <div className="grid items-start gap-[12px] lg:grid-cols-2">
                    {first ? <span className="sr-only">Loading agents…</span> : null}
                    {first
                        ? agents.map((agent) => <AgentCardLoading key={agent.id} definition={agent} />)
                        : rows.map((item) => <AgentCard key={item.definition.id} item={item} onNavigate={onNavigate} />)}
                </div>
            </section>

        </div>
    );
}
